import { useEffect, useRef } from "react";

import {
  computeToolCallStatus,
  ToolCallAccordionItem,
} from "src/base-components/agents/ToolCallDisplay";
import { ChatMessage } from "src/base-components/Chat/ChatMessage";
import {
  type SessionMessagesResponseMessagesInner,
  type ToolMessage,
} from "src/clients/agent-management";
import { type LiveAnalyticsChatStatus } from "src/flow/analytics/m1/useLiveAnalyticsChat";

const renderHumanContent = (content: unknown): string => {
  if (typeof content === "string") return content;
  return JSON.stringify(content);
};

const buildToolResultMap = (
  messages: SessionMessagesResponseMessagesInner[],
): Map<string, ToolMessage> => {
  const entries = messages
    .filter((m): m is { type: "tool" } & ToolMessage => m.type === "tool")
    .map((m) => [m.tool_call_id, m] as const);
  return new Map(entries);
};

export const LiveAnalyticsMessages: React.FC<{
  messages: SessionMessagesResponseMessagesInner[];
  status: LiveAnalyticsChatStatus;
  errorMessage: string | null;
}> = ({ messages, status, errorMessage }) => {
  const toolResults = buildToolResultMap(messages);
  const tailRef = useRef<HTMLLIElement>(null);

  // Sync scroll position with the bottom of the list whenever new content
  // arrives. External DOM read/write — valid useEffect per CLAUDE.md.
  useEffect(() => {
    tailRef.current?.scrollIntoView({ block: "end" });
  }, [messages.length, status]);

  const sessionSubstatus = status === "thinking" ? "running" : null;

  const renderableMessages = messages.filter(
    (m) => m.type === "human" || m.type === "ai",
  );

  return (
    <>
      {renderableMessages.map((message, idx) => {
        const key = message.id ?? `${message.type}-${idx}`;
        if (message.type === "human") {
          return (
            <ChatMessage key={key} dataLoc="copilot-message-user" role="user">
              <div className="rounded-2xl bg-gray-50 px-3 py-2 font-inter-normal-13px text-gray-800">
                {renderHumanContent(message.content)}
              </div>
            </ChatMessage>
          );
        }
        // AI message
        return (
          <ChatMessage key={key} dataLoc="copilot-message-ai" role="assistant">
            <div className="flex flex-col gap-2">
              {(message.content_blocks ?? []).map((block, blockIdx) => {
                if (block.type === "text") {
                  return (
                    <p
                      key={`${key}-text-${blockIdx}`}
                      className="whitespace-pre-wrap font-inter-normal-13px text-gray-800"
                    >
                      {block.text}
                    </p>
                  );
                }
                const result = toolResults.get(block.id) ?? null;
                return (
                  <ToolCallAccordionItem
                    key={`${key}-tool-${block.id}`}
                    status={computeToolCallStatus(result, sessionSubstatus)}
                    toolCall={block}
                    toolResult={result}
                  />
                );
              })}
            </div>
          </ChatMessage>
        );
      })}
      {status === "thinking" && (
        <li
          ref={tailRef}
          className="font-inter-normal-12px text-gray-500"
          data-loc="copilot-thinking"
        >
          Thinking…
        </li>
      )}
      {status === "error" && errorMessage && (
        <li
          ref={tailRef}
          className="font-inter-normal-12px text-red-600"
          data-loc="copilot-error"
        >
          {errorMessage}
        </li>
      )}
      {status !== "thinking" && status !== "error" && (
        <li ref={tailRef} aria-hidden="true" className="h-0" />
      )}
    </>
  );
};
